import {
  Button,
  OutlinedSelect,
  OutlinedSelectOptionType,
  Table,
  addToast,
  showDialogConfirm,
} from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import Loading from '../../../../../components/info/Loading';
import SectionTitle from '../../../../../components/text/SectionTitle';
import TableEmptyContent from '../../../../../components/text/TableEmptyContent';

import {
  CollectionFeeOverview,
  CollectionFeeRuleItem,
  deleteCollectionFeeRule,
  getCollectionFees,
  updateCollectionFeeCapitalizes,
} from './api';
import { ConfigModal } from './components/ConfigModal';
import { useRuleColumns } from './hooks/useRuleColumns';

const dictPrefix = 'financialPortal.settings.collectionFees';

// `value` va como string ('yes' | 'no') en la opción del Select: sirve de
// key i18n y evita usar `true`/`false` como `value` de un OutlinedSelectOptionType
// (tipado como string | number).
const CAPITALIZES_VALUES: Array<{ key: 'yes' | 'no'; value: boolean }> = [
  { key: 'yes', value: true },
  { key: 'no', value: false },
];

export default function CollectionFeesSection() {
  const { t } = useTranslation();
  const [overview, setOverview] = useState<CollectionFeeOverview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<CollectionFeeRuleItem | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await getCollectionFees();
      if (cancelled) {
        return;
      }
      if (error || !data) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${dictPrefix}.loadError`),
        });
      } else {
        setOverview(data);
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const handleSaved = useCallback(() => {
    setIsModalOpen(false);
    setEditing(null);
    setRefreshToken((prev) => prev + 1);
  }, []);

  const handleNew = useCallback(() => {
    setEditing(null);
    setIsModalOpen(true);
  }, []);

  const handleEdit = useCallback((item: CollectionFeeRuleItem) => {
    setEditing(item);
    setIsModalOpen(true);
  }, []);

  const handleDelete = useCallback(
    (item: CollectionFeeRuleItem) => {
      showDialogConfirm({
        icon: { name: 'warning', color: 'warning' },
        title: t(`${dictPrefix}.confirmDelete.title`),
        subtitle: t(`${dictPrefix}.confirmDelete.subtitle`),
        btnConfirm: {
          text: t(`${dictPrefix}.confirmDelete.confirm`),
          onConfirm: async () => {
            const { error } = await deleteCollectionFeeRule(item.id);
            if (error) {
              addToast({
                icon: 'error',
                color: 'danger',
                text: t(`${dictPrefix}.deleteError`),
              });
              return;
            }
            addToast({
              icon: 'success',
              color: 'success',
              text: t(`${dictPrefix}.deleteSuccess`),
            });
            setRefreshToken((prev) => prev + 1);
          },
        },
        btnClose: { text: t(`${dictPrefix}.confirmDelete.cancel`) },
      });
    },
    [t],
  );

  const columns = useRuleColumns({ onEdit: handleEdit, onDelete: handleDelete });

  const capitalizesOptions = useMemo<OutlinedSelectOptionType[]>(
    () =>
      CAPITALIZES_VALUES.map(({ key }) => ({
        value: key,
        label: t(`${dictPrefix}.capitalizesOptions.${key}`),
      })),
    [t],
  );

  const handleCapitalizesChange = useCallback(
    async (option: OutlinedSelectOptionType | null) => {
      if (!option) {
        return;
      }
      const capitalizes =
        CAPITALIZES_VALUES.find((entry) => entry.key === option.value)?.value ??
        false;
      const { error } = await updateCollectionFeeCapitalizes(capitalizes);
      if (error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${dictPrefix}.capitalizesError`),
        });
        return;
      }
      addToast({
        icon: 'success',
        color: 'success',
        text: t(`${dictPrefix}.capitalizesSaved`),
      });
      setRefreshToken((prev) => prev + 1);
    },
    [t],
  );

  if (isLoading) {
    return <Loading insideCard />;
  }
  if (!overview) {
    return null;
  }

  return (
    <>
      <Row className="align-items-start">
        <Col>
          <SectionTitle text={t(`${dictPrefix}.title`)} />
          <p>{t(`${dictPrefix}.subtitle`)}</p>
        </Col>
        <Col xs="auto" style={{ minWidth: 280 }}>
          <OutlinedSelect
            name="capitalizes"
            label={t(`${dictPrefix}.capitalizesLabel`)}
            options={capitalizesOptions}
            value={capitalizesOptions.find(
              (option) => option.value === (overview.capitalizes ? 'yes' : 'no'),
            )}
            onChange={handleCapitalizesChange}
            isClearable={false}
          />
        </Col>
        <Col xs="auto">
          <Button
            text={t(`${dictPrefix}.newConfig`)}
            onClick={handleNew}
            size="sm"
            className="mb-1"
            outlined
          />
        </Col>
      </Row>
      <Table
        columns={columns}
        data={overview.items}
        noResultsText={
          <>
            <TableEmptyContent
              title={t(`${dictPrefix}.empty.title`)}
              subtitle={t(`${dictPrefix}.empty.subtitle`)}
            />
            <Button
              text={t(`${dictPrefix}.empty.action`)}
              onClick={handleNew}
              outlined
              size="sm"
              className="mx-auto mb-4"
            />
          </>
        }
      />
      <ConfigModal
        isOpen={isModalOpen}
        item={editing}
        onClose={() => setIsModalOpen(false)}
        onSaved={handleSaved}
      />
    </>
  );
}
